import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle, DownloadSimple, FilePdf, FolderSimplePlus, HandPalm, UploadSimple, WarningCircle, X } from '@phosphor-icons/react';
import { DialogHuelle, ImmoButton, ObjektPicker, type ObjektPickerOption } from '../../../../components/immobilien/ui';
import { ladeOrdnerBaum, type BaumKnoten } from '../../../../lib/dokumente/aktenschrankApi';
import { flacheOrdner } from '../../../../lib/dokumente/aktenschrank';
import { legeSigniertInAkte, schliesseVonHandAb, speichereBlob } from '../../../../lib/dokumente/signaturApi';
import { fehlendeUnterzeichner } from '../../../../lib/signatur/vorgangAktionen';
import type { SignaturVorgang } from '../../../../lib/dokumente/signaturTypen';
import type { AktObjekt } from '../../../../lib/dokumente/useAktenschrank';
import { OrdnerAuswahl } from '../ablage/OrdnerAuswahl';

const fehlerText = (e: unknown, sonst: string) => (e instanceof Error && e.message ? e.message : sonst);
const MAX_MB = 20;

const FELD_LABEL: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: 'var(--ink3)' };

/** In Akte ablegen (Handoff §6.4): Objekt (vorbelegt aus dem Bezug) und Zielordner aus dem Baum, Upload ins Drive. */
export function AkteAblegenDialog({ vorgang, objekte, onSchliessen, onFertig }: {
  vorgang: SignaturVorgang; objekte: readonly AktObjekt[]; onSchliessen: () => void; onFertig: (meldung: string) => void;
}) {
  const [objektId, setObjektId] = useState<string | null>(vorgang.bezug?.objekt_id ?? null);
  const [baum, setBaum] = useState<BaumKnoten[] | null>(null);
  const [ordnerId, setOrdnerId] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const objekt = objekte.find(o => o.id === objektId) ?? null;
  const optionen: ObjektPickerOption[] = useMemo(() => objekte.map(o => ({
    id: o.id, label: o.name, nummer: o.nummer, sub: [o.verwaltungsart, o.ort].filter(Boolean).join(' · '), suchtext: `${o.nummer} ${o.ort}`,
    hinweis: o.driveId ? undefined : 'kein Drive-Ordner',
  })), [objekte]);

  useEffect(() => {
    setBaum(null); setOrdnerId('');
    if (!objektId) return;
    let aktiv = true;
    ladeOrdnerBaum(objektId).then(b => { if (aktiv) setBaum(b); }).catch((e: unknown) => { if (aktiv) setFehler(fehlerText(e, 'Ordner nicht geladen')); });
    return () => { aktiv = false; };
  }, [objektId]);
  const flach = useMemo(() => (baum ? flacheOrdner(baum) : []), [baum]);
  const ordnerIdNachPfad = useMemo(() => {
    const m = new Map<string, string>();
    const lauf = (l: readonly BaumKnoten[]) => l.forEach(k => { m.set(k.pfad, k.gdriveId); lauf(k.kinder); });
    if (baum) lauf(baum);
    return m;
  }, [baum]);
  const pfadNachOrdnerId = useMemo(() => new Map([...ordnerIdNachPfad].map(([pfad, id]) => [id, pfad])), [ordnerIdNachPfad]);

  const ablegen = async () => {
    if (!ordnerId) return;
    setLaeuft(true); setFehler(null);
    try {
      await legeSigniertInAkte(vorgang, ordnerId);
      onFertig(`„${vorgang.titel}.pdf“ in der Akte abgelegt`);
    } catch (e) { setFehler(fehlerText(e, 'Ablegen fehlgeschlagen')); setLaeuft(false); }
  };

  const hinweis = !objektId ? 'Bitte ein Objekt wählen.' : objekt && !objekt.driveId ? 'Objekt hat keinen Drive-Ordner.' : !ordnerId ? 'Bitte einen Zielordner wählen.' : null;
  return (
    <DialogHuelle titel="In Akte ablegen" untertitel={`${vorgang.titel}.pdf`} icon={FolderSimplePlus} onSchliessen={onSchliessen} laeuft={laeuft}
      fuss={<>
        <ImmoButton art="fuellung" groesse={44} pille={false} onClick={onSchliessen} disabled={laeuft} style={{ flex: 1 }}>Abbrechen</ImmoButton>
        <ImmoButton art="akzent" groesse={44} pille={false} icon={FolderSimplePlus} onClick={() => void ablegen()} disabled={laeuft || !!hinweis} style={{ flex: 1 }}>{laeuft ? 'Wird abgelegt …' : 'Ablegen'}</ImmoButton>
      </>}>
      <div className="flex flex-col" style={{ gap: 6 }}>
        <span style={FELD_LABEL}>Objekt</span>
        <div><ObjektPicker optionen={optionen} wert={objektId} onWahl={setObjektId} /></div>
      </div>
      <div className="flex flex-col" style={{ gap: 6 }}>
        <span style={FELD_LABEL}>Zielordner</span>
        {/* #518: Auswahl mit Suche und farbig abgesetzten Hauptordnern */}
        <OrdnerAuswahl optionen={flach} wert={pfadNachOrdnerId.get(ordnerId) ?? ''} onWahl={pfad => setOrdnerId(ordnerIdNachPfad.get(pfad) ?? '')}
          disabled={!objektId || laeuft || !baum}
          platzhalter={!objektId ? 'Zuerst ein Objekt wählen' : !baum ? 'Ordner werden geladen …' : 'Ordner wählen …'} />
      </div>
      {(fehler || hinweis) && (
        <span className="flex items-start" style={{ gap: 6, fontSize: 12, color: fehler ? 'var(--bad)' : 'var(--ink3)' }}>
          <WarningCircle weight="fill" size={14} style={{ flexShrink: 0, marginTop: 1 }} />{fehler ?? hinweis}
        </span>
      )}
    </DialogHuelle>
  );
}

/** Von Hand abgeschlossen: gegengezeichnetes PDF (empfohlen) und Notiz; danach steht der Vermerk an der Karte. */
export function VonHandDialog({ vorgang, onSchliessen, onFertig }: { vorgang: SignaturVorgang; onSchliessen: () => void; onFertig: (meldung: string) => void }) {
  const [notiz, setNotiz] = useState('');
  const [datei, setDatei] = useState<File | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const offene = fehlendeUnterzeichner(vorgang.unterzeichner);

  const waehle = (f: File | null) => {
    setFehler(null);
    if (!f) { setDatei(null); return; }
    if (f.type !== 'application/pdf') { setFehler('Bitte eine PDF-Datei wählen.'); return; }
    if (f.size > MAX_MB * 1024 * 1024) { setFehler(`Die Datei ist größer als ${MAX_MB} MB.`); return; }
    setDatei(f);
  };
  const abschliessen = async () => {
    setLaeuft(true); setFehler(null);
    try { await schliesseVonHandAb(vorgang, notiz, datei); onFertig(`„${vorgang.titel}“ als abgeschlossen geführt`); }
    catch (e) { setFehler(fehlerText(e, 'Abschluss fehlgeschlagen')); setLaeuft(false); }
  };

  return (
    <DialogHuelle titel="Von Hand abgeschlossen" untertitel={vorgang.titel} icon={HandPalm} onSchliessen={onSchliessen} laeuft={laeuft}
      fuss={<>
        <ImmoButton art="fuellung" groesse={44} pille={false} onClick={onSchliessen} disabled={laeuft} style={{ flex: 1 }}>Abbrechen</ImmoButton>
        <ImmoButton art="akzent" groesse={44} pille={false} icon={CheckCircle} onClick={() => void abschliessen()} disabled={laeuft} style={{ flex: 1 }}>{laeuft ? 'Wird abgeschlossen …' : 'Als abgeschlossen führen'}</ImmoButton>
      </>}>
      <span style={{ fontSize: 13, color: 'var(--ink2)' }}>„{vorgang.titel}“ wird als abgeschlossen geführt, ohne dass digital zu Ende signiert wird.</span>
      {offene.length > 0 && (
        <span className="immo-innen" style={{ borderRadius: 14, padding: '10px 12px', fontSize: 12, color: 'var(--ink2)' }}>
          Digital fehlt noch: <strong style={{ color: 'var(--ink)' }}>{offene.join(', ')}</strong>. Der Signier-Link wird damit ungültig.
        </span>
      )}
      <div className="flex flex-col" style={{ gap: 6 }}>
        <span style={FELD_LABEL}>Gegengezeichnetes PDF (empfohlen)</span>
        {datei ? (
          <span className="immo-innen flex items-center" style={{ borderRadius: 14, padding: '0 6px 0 12px', gap: 8, minHeight: 44 }}>
            <FilePdf weight="fill" size={16} color="var(--bad)" />
            <span className="flex-1 min-w-0 truncate" style={{ fontSize: 13, fontWeight: 700 }}>{datei.name}</span>
            <button type="button" aria-label="Datei entfernen" onClick={() => setDatei(null)} className="flex items-center justify-center cursor-pointer"
              style={{ width: 44, height: 44, border: 'none', borderRadius: 12, background: 'transparent', color: 'var(--ink2)' }}><X weight="bold" size={13} /></button>
          </span>
        ) : (
          <label className="immo-hover-flaeche flex items-center justify-center cursor-pointer" style={{ minHeight: 52, borderRadius: 14, gap: 8, fontSize: 13, fontWeight: 700, color: 'var(--ink2)', boxShadow: 'inset 0 0 0 1.5px var(--line)', borderStyle: 'dashed' }}>
            <UploadSimple weight="bold" size={15} />PDF wählen (max. {MAX_MB} MB)
            <input type="file" accept="application/pdf" hidden onChange={e => waehle(e.target.files?.[0] ?? null)} />
          </label>
        )}
        <span style={{ fontSize: 11, color: 'var(--ink3)' }}>Ohne Datei bleibt nur der Vermerk – Herunterladen und Ablegen stehen dann nicht zur Verfügung.</span>
      </div>
      <label className="flex flex-col" style={{ gap: 6 }}>
        <span style={FELD_LABEL}>Notiz</span>
        <textarea value={notiz} onChange={e => setNotiz(e.target.value)} rows={3} placeholder="z. B. am 09.09.2026 im Büro gegengezeichnet"
          style={{ borderRadius: 14, border: 'none', padding: '10px 14px', fontSize: 14, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--ink)', boxShadow: 'inset 0 0 0 1px var(--line)', resize: 'vertical' }} />
      </label>
      {fehler && <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--bad)' }}>{fehler}</span>}
    </DialogHuelle>
  );
}

/** #600: Zwischenstand in der App ansehen (statt Direkt-Download, den manche Browser/App-Fenster selbst öffnen). */
export function ZwischenstandDialog({ datei, titel, onSchliessen }: { datei: { blob: Blob; name: string }; titel: string; onSchliessen: () => void }) {
  const url = useMemo(() => URL.createObjectURL(datei.blob), [datei.blob]);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  return (
    <DialogHuelle titel="Zwischenstand" untertitel={`${titel} – noch nicht vollständig signiert`} icon={FilePdf} onSchliessen={onSchliessen} breite={980}
      fuss={(
        <div className="flex w-full justify-end" style={{ gap: 8 }}>
          <ImmoButton onClick={onSchliessen}>Schließen</ImmoButton>
          <ImmoButton art="akzent" icon={DownloadSimple} onClick={() => speichereBlob(datei.blob, datei.name)}>Herunterladen</ImmoButton>
        </div>
      )}>
      <iframe title={`Zwischenstand ${titel}`} src={`${url}#view=FitH`}
        style={{ width: '100%', height: 'min(72vh, 900px)', border: 'none', borderRadius: 12, background: '#fff' }} />
    </DialogHuelle>
  );
}
